/**
 * Keeps a capped product's add-to-cart UI in sync with the *live* cart.
 *
 * The Liquid render already sets the correct remaining allowance at page-load time
 * (snippets/order-limit-remaining.liquid, reading the custom.order_limit metafield
 * against cart.items). This script re-checks that allowance against a fresh /cart.js
 * fetch whenever the cart could have changed without this page re-rendering -
 * drawer edits, another tab, or the tab regaining focus - so the quantity cap and
 * "Add to cart" button never fall out of sync with what's actually in the cart.
 */
(function () {
  // productId -> quantity submitted but not yet confirmed by a /cart.js refresh.
  // Needed because aria-disabled (set synchronously by product.js's own submit
  // handler) does NOT actually block a second form submission - only the real
  // `disabled` attribute does, and that was only ever applied reactively after
  // a full cart round-trip. Without this, clicking Add to Cart faster than
  // that round-trip lets a shopper exceed the cap.
  const pending = new Map();

  function getProductForms() {
    return Array.from(document.querySelectorAll('product-form[data-order-limit-product-id]'));
  }

  function refresh(productForm) {
    const productId = parseInt(productForm.dataset.orderLimitProductId, 10);
    const cap = parseInt(productForm.dataset.orderLimitCap, 10);
    if (!productId || !cap) return;

    fetch(`${theme.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const held = cart.items
          .filter((item) => item.product_id === productId)
          .reduce((sum, item) => sum + item.quantity, 0);
        // This fetch reflects real, confirmed server state - any optimistic
        // guess made since the last refresh is now stale, drop it.
        pending.delete(productId);
        const remaining = Math.max(cap - held, 0);
        applyRemaining(productForm, cap, remaining);
      })
      .catch((e) => console.error(e));
  }

  function applyRemaining(productForm, cap, remaining) {
    const quantitySelector = productForm.querySelector('quantity-selector[data-order-limit-remaining]');
    const quantityWrapper = quantitySelector?.closest('[id^="quantity-"]');
    const input = quantitySelector?.querySelector('.qty');
    const submitButtons = productForm.querySelectorAll('.single-add-to-cart-button');
    const message = productForm.querySelector('[data-order-limit-message]');
    const reached = remaining === 0;

    if (quantitySelector) quantitySelector.dataset.orderLimitRemaining = remaining;
    if (input) {
      input.max = remaining;
      if (parseInt(input.value, 10) > remaining) input.value = Math.max(remaining, parseInt(input.min, 10) || 0);
    }
    if (quantityWrapper) quantityWrapper.style.display = reached ? 'none' : '';

    submitButtons.forEach((submitButton) => {
      submitButton.disabled = reached;
      submitButton.toggleAttribute('disabled', reached);
      const text = submitButton.querySelector('.single-add-to-cart-button--text');
      if (text && reached) text.textContent = 'Limit Reached';
      else if (text && text.textContent === 'Limit Reached') text.textContent = window.theme?.variantStrings?.addToCart || 'Add to cart';
    });

    if (message) {
      message.classList.toggle('error', reached);
      message.textContent = reached
        ? `You already have the max (${cap}) of this item in your cart.`
        : `Limit ${cap} per customer${remaining < cap ? ` — ${remaining} remaining` : ''}.`;
    }
  }

  function refreshAll() {
    getProductForms().forEach(refresh);
  }

  // Capture phase so this runs BEFORE product.js's own submit handler (and
  // regardless of script load order) on every form submission, gating each
  // attempt against confirmed-remaining minus whatever's already in flight.
  document.addEventListener('submit', function (evt) {
    const productForm = evt.target.closest && evt.target.closest('product-form[data-order-limit-product-id]');
    if (!productForm) return;

    const productId = parseInt(productForm.dataset.orderLimitProductId, 10);
    const cap = parseInt(productForm.dataset.orderLimitCap, 10);
    const quantitySelector = productForm.querySelector('quantity-selector[data-order-limit-remaining]');
    const confirmedRemaining = quantitySelector
      ? parseInt(quantitySelector.dataset.orderLimitRemaining, 10)
      : cap;
    const alreadyPending = pending.get(productId) || 0;
    const effectiveRemaining = Math.max(confirmedRemaining - alreadyPending, 0);

    if (effectiveRemaining <= 0) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      applyRemaining(productForm, cap, 0);
      return;
    }

    const qtyInput = evt.target.querySelector('input[name="quantity"]');
    const requestedQty = qtyInput ? Math.max(parseInt(qtyInput.value, 10) || 1, 1) : 1;

    pending.set(productId, alreadyPending + requestedQty);
    applyRemaining(productForm, cap, Math.max(effectiveRemaining - requestedQty, 0));
  }, true);

  // A failed add never fires cart:item-added, so pending would otherwise stay
  // permanently inflated for that product - clear and re-sync from the server.
  document.addEventListener('product:variant-error', () => {
    pending.clear();
    refreshAll();
  });

  // ─── Checkout guard - covers both the cart drawer AND the full /cart page ──
  // Belt-and-suspenders: even with the +/- stepper max fixed above, block
  // checkout entirely if any capped line is somehow still over its limit
  // (an old cart from before this fix, a future edge case, etc). Reads the
  // max/value already rendered on each line's quantity input directly -
  // no need to re-derive caps in JS, since /cart.js doesn't expose product
  // metafields but both cart-drawer.liquid and main-cart.liquid's own Liquid
  // render already does. Deliberately NOT scoped to .cart-drawer - the two
  // surfaces (drawer vs. full page) use different markup entirely (a
  // #cart-form submit vs. a click inside .cart-drawer--buttons-container),
  // so this checks every capped quantity input on the page regardless of
  // which surface is currently in view.
  function anyOrderLimitViolation() {
    return Array.from(document.querySelectorAll('input.qty[max]')).some((input) => {
      const max = parseInt(input.max, 10);
      const val = parseInt(input.value, 10);
      return !isNaN(max) && !isNaN(val) && val > max;
    });
  }

  // Second, separate backstop: a still-pre-release product should never be
  // checkout-able regardless of how it got into the cart. Add-to-cart-time
  // gates exist in product-add-to-cart.liquid, product-add-to-cart-sticky.
  // liquid, and product-card.liquid, but NONE of them protect an item that's
  // already sitting in the cart from a bypass (known, since-fixed, or not
  // yet found), a stale cached page, or a sales channel (Shop Pay/the Shop
  // app, social shopping) that reads Shopify's own availability status
  // directly and never renders our theme's gate at all - confirmed live
  // 2026-08-27, hours before a release, on the exact product this exists to
  // protect. Reads a data-timer-future flag computed server-side per line
  // in cart-drawer.liquid/main-cart.liquid (same custom.timer metafield
  // logic as the add-to-cart gates) - it's a hard stop, not a UX nicety.
  function anyTimerViolation() {
    return document.querySelector('[data-timer-future="true"]') !== null;
  }

  function blockIfOverLimit(evt) {
    if (anyTimerViolation()) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      alert('One or more items in your cart are not available for purchase until their release date. Please remove them before checking out.');
      return;
    }
    if (!anyOrderLimitViolation()) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
    alert('Please reduce the quantity of a limited item in your cart before checking out.');
  }

  // .nvd-mini is where Navidium renders its own Protected Checkout / Checkout
  // without protection buttons on BOTH surfaces (app-injected, not theme
  // markup - see snippets/cart-drawer.liquid and sections/main-cart.liquid);
  // .cart-drawer--buttons-container is our own drawer View Cart/Checkout;
  // .thb-cart-form--proceed is the full /cart page's checkout button
  // wrapper (its button uses form="cart-form", not a descendant <button
  // type=submit>, but the click still needs catching before it fires the
  // form's submit). Capture phase catches both click-driven and link-driven
  // checkout triggers before they can navigate or submit.
  //
  // IMPORTANT LIMITATION, confirmed live 2026-08-27: this click/submit guard
  // CANNOT catch Shopify's native accelerated checkout buttons (Shop Pay,
  // Apple Pay, Google Pay, PayPal - rendered via
  // {{ content_for_additional_checkout_buttons }} in sections/main-cart.liquid
  // when the merchant has "Dynamic checkout buttons" enabled in Admin). Those
  // render inside a cross-origin iframe / closed-shadow-DOM web component for
  // PCI compliance, and Apple Pay specifically uses the native ApplePaySession
  // browser API (an OS-level payment sheet) - none of that dispatches a
  // regular 'click'/'submit' event a page-level listener can see or
  // preventDefault on. This is almost certainly how the pre-release bypass
  // actually happened. The click/submit guard above still helps for our own
  // real checkout button and Navidium's, but it is NOT sufficient on its own.
  // removeTimerGatedLines() below is the real fix: it strips a pre-release
  // item from the cart entirely, so by the time ANY checkout button -
  // accelerated or not - is rendered, there is nothing left to buy.
  document.addEventListener('click', function (evt) {
    if (evt.target.closest && evt.target.closest('.nvd-mini, .cart-drawer--buttons-container, .thb-cart-form--proceed')) {
      blockIfOverLimit(evt);
    }
  }, true);
  document.addEventListener('submit', function (evt) {
    if (evt.target.closest && (evt.target.closest('.cart-drawer') || evt.target.id === 'cart-form')) {
      blockIfOverLimit(evt);
    }
  }, true);

  // Actively ejects any still-pre-release line from the cart the moment it's
  // detected - the real fix for the accelerated-checkout gap above. Uses
  // data-cart-key (the line's cart.js `key`, valid as the `id` for
  // /cart/change.js) rendered alongside data-timer-future in
  // cart-drawer.liquid/main-cart.liquid. Runs before the click/submit guard
  // would ever matter, on every page load and cart-refresh event, so the
  // item can't persist in the cart long enough for a customer to reach any
  // checkout button.
  let removingTimerLines = false;
  function removeTimerGatedLines() {
    if (removingTimerLines) return;
    const lines = Array.from(document.querySelectorAll('[data-timer-future="true"][data-cart-key]'));
    if (!lines.length) return;
    removingTimerLines = true;

    const removals = lines.map((el) =>
      fetch(`${theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: el.dataset.cartKey, quantity: 0 })
      }).catch((e) => console.error('Failed to remove pre-release line item:', e))
    );

    Promise.all(removals).then(() => {
      // Remove the elements directly rather than dispatching cart:refresh -
      // that would trigger cart.js's own async section re-render, which could
      // still be mid-flight when this function's own listeners re-check,
      // re-finding the same (already stale) markup and looping.
      lines.forEach((el) => el.remove());
      alert('One or more items in your cart are not available for purchase until their release date and have been removed.');
    }).finally(() => {
      removingTimerLines = false;
    });
  }

  function onCartMayHaveChanged() {
    refreshAll();
    removeTimerGatedLines();
  }

  document.addEventListener('DOMContentLoaded', onCartMayHaveChanged);
  window.addEventListener('focus', onCartMayHaveChanged);
  document.addEventListener('cart:refresh', onCartMayHaveChanged);
  document.addEventListener('cart:item-added', onCartMayHaveChanged);
  document.addEventListener('line-item:change:end', onCartMayHaveChanged);

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    onCartMayHaveChanged();
  }
})();
